/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  CreateSiteFromTemplateRequest,
  CreateSiteTemplateRequest,
  Error,
  ListSiteTemplatesParams,
  ListSiteTemplatesResponse,
  SiteTemplateResponse,
  SuccessResponse,
  TaskIDResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Starts a background job to snapshot an existing site (plugins, theme, content) as a reusable template. Source site must be under 4 GB.
 * @summary Create a site template
 */
export const createSiteTemplate = (
  createSiteTemplateRequest: CreateSiteTemplateRequest,
  signal?: AbortSignal
) => {
  return customFetch<TaskIDResponse>({
    url: `/v1/site_templates`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSiteTemplateRequest,
    signal,
  });
};

export const getCreateSiteTemplateMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteTemplate>>,
    TError,
    { data: CreateSiteTemplateRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteTemplate>>,
  TError,
  { data: CreateSiteTemplateRequest },
  TContext
> => {
  const mutationKey = ['createSiteTemplate'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteTemplate>>,
    { data: CreateSiteTemplateRequest }
  > = props => {
    const { data } = props ?? {};

    return createSiteTemplate(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/site_templates` },
    {
      operationId: 'app.controllers.site_templates_controller.create_site_template',
      operationName: 'createSiteTemplate',
    }
  );

  return customOptions;
};

export type CreateSiteTemplateMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteTemplate>>
>;
export type CreateSiteTemplateMutationBody = CreateSiteTemplateRequest;
export type CreateSiteTemplateMutationError = Error;

/**
 * @summary Create a site template
 */
export const useCreateSiteTemplate = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteTemplate>>,
      TError,
      { data: CreateSiteTemplateRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteTemplate>>,
  TError,
  { data: CreateSiteTemplateRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteTemplateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists reusable site templates. Filter by source site, domain, or tags.
 * @summary List site templates
 */
export const listSiteTemplates = (params?: ListSiteTemplatesParams, signal?: AbortSignal) => {
  return customFetch<ListSiteTemplatesResponse>({
    url: `/v1/sites/templates`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListSiteTemplatesQueryKey = (params?: ListSiteTemplatesParams) => {
  return [`/v1/sites/templates`, ...(params ? [params] : [])] as const;
};

export const getListSiteTemplatesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteTemplatesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteTemplates>>> = ({ signal }) =>
    listSiteTemplates(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/sites/templates`,
      operationId: 'app.controllers.site_templates_controller.list_site_templates',
      operationName: 'listSiteTemplates',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listSiteTemplates>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteTemplatesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteTemplates>>
>;
export type ListSiteTemplatesQueryError = Error;

export function useListSiteTemplates<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params: undefined | ListSiteTemplatesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteTemplates>>,
          TError,
          Awaited<ReturnType<typeof listSiteTemplates>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteTemplates<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteTemplates>>,
          TError,
          Awaited<ReturnType<typeof listSiteTemplates>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteTemplates<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site templates
 */

export function useListSiteTemplates<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteTemplatesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListSiteTemplatesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteTemplatesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteTemplates>>> = ({ signal }) =>
    listSiteTemplates(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/sites/templates`,
      operationId: 'app.controllers.site_templates_controller.list_site_templates',
      operationName: 'listSiteTemplates',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listSiteTemplates>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteTemplatesSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteTemplates>>
>;
export type ListSiteTemplatesSuspenseQueryError = Error;

export function useListSiteTemplatesSuspense<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params: undefined | ListSiteTemplatesParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteTemplatesSuspense<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteTemplatesSuspense<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site templates
 */

export function useListSiteTemplatesSuspense<
  TData = Awaited<ReturnType<typeof listSiteTemplates>>,
  TError = Error,
>(
  params?: ListSiteTemplatesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteTemplates>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteTemplatesSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Deprecated. Use POST /site_templates instead, which runs as a background task.
 * @summary [Deprecated] Create site template
 */
export const createSiteTemplateDeprecated = (
  createSiteTemplateRequest: CreateSiteTemplateRequest,
  signal?: AbortSignal
) => {
  return customFetch<SiteTemplateResponse>({
    url: `/v1/sites/templates`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSiteTemplateRequest,
    signal,
  });
};

export const getCreateSiteTemplateDeprecatedMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteTemplateDeprecated>>,
    TError,
    { data: CreateSiteTemplateRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteTemplateDeprecated>>,
  TError,
  { data: CreateSiteTemplateRequest },
  TContext
> => {
  const mutationKey = ['createSiteTemplateDeprecated'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteTemplateDeprecated>>,
    { data: CreateSiteTemplateRequest }
  > = props => {
    const { data } = props ?? {};

    return createSiteTemplateDeprecated(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/templates` },
    {
      operationId: 'app.controllers.site_templates_controller.create_site_template_deprecated',
      operationName: 'createSiteTemplateDeprecated',
    }
  );

  return customOptions;
};

export type CreateSiteTemplateDeprecatedMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteTemplateDeprecated>>
>;
export type CreateSiteTemplateDeprecatedMutationBody = CreateSiteTemplateRequest;
export type CreateSiteTemplateDeprecatedMutationError = Error;

/**
 * @summary [Deprecated] Create site template
 */
export const useCreateSiteTemplateDeprecated = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteTemplateDeprecated>>,
      TError,
      { data: CreateSiteTemplateRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteTemplateDeprecated>>,
  TError,
  { data: CreateSiteTemplateRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteTemplateDeprecatedMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns one template's name, source site, WordPress config, tags, and notes.
 * @summary Get site template details
 */
export const findSiteTemplate = (templateId: string, signal?: AbortSignal) => {
  return customFetch<SiteTemplateResponse>({
    url: `/v1/sites/templates/${templateId}`,
    method: 'GET',
    signal,
  });
};

export const getFindSiteTemplateQueryKey = (templateId?: string) => {
  return [`/v1/sites/templates/${templateId}`] as const;
};

export const getFindSiteTemplateQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteTemplateQueryKey(templateId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteTemplate>>> = ({ signal }) =>
    findSiteTemplate(templateId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { templateId },
    {
      url: `/v1/sites/templates/${templateId}`,
      operationId: 'app.controllers.site_templates_controller.find_site_template',
      operationName: 'findSiteTemplate',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findSiteTemplate>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof findSiteTemplate>>>;
export type FindSiteTemplateQueryError = Error;

export function useFindSiteTemplate<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteTemplate>>,
          TError,
          Awaited<ReturnType<typeof findSiteTemplate>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteTemplate<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findSiteTemplate>>,
          TError,
          Awaited<ReturnType<typeof findSiteTemplate>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteTemplate<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get site template details
 */

export function useFindSiteTemplate<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteTemplateQueryOptions(templateId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindSiteTemplateSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindSiteTemplateQueryKey(templateId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findSiteTemplate>>> = ({ signal }) =>
    findSiteTemplate(templateId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { templateId },
    {
      url: `/v1/sites/templates/${templateId}`,
      operationId: 'app.controllers.site_templates_controller.find_site_template',
      operationName: 'findSiteTemplate',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findSiteTemplate>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindSiteTemplateSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findSiteTemplate>>
>;
export type FindSiteTemplateSuspenseQueryError = Error;

export function useFindSiteTemplateSuspense<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteTemplateSuspense<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindSiteTemplateSuspense<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get site template details
 */

export function useFindSiteTemplateSuspense<
  TData = Awaited<ReturnType<typeof findSiteTemplate>>,
  TError = Error,
>(
  templateId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findSiteTemplate>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindSiteTemplateSuspenseQueryOptions(templateId, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Permanently deletes a site template from your account.
 * @summary Delete a site template
 */
export const deleteSiteTemplate = (templateId: string) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/templates/${templateId}`,
    method: 'DELETE',
  });
};

export const getDeleteSiteTemplateMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSiteTemplate>>,
    TError,
    { templateId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSiteTemplate>>,
  TError,
  { templateId: string },
  TContext
> => {
  const mutationKey = ['deleteSiteTemplate'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSiteTemplate>>,
    { templateId: string }
  > = props => {
    const { templateId } = props ?? {};

    return deleteSiteTemplate(templateId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/templates/{templateId}` },
    {
      operationId: 'app.controllers.site_templates_controller.delete_site_template',
      operationName: 'deleteSiteTemplate',
    }
  );

  return customOptions;
};

export type DeleteSiteTemplateMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSiteTemplate>>
>;

export type DeleteSiteTemplateMutationError = Error;

/**
 * @summary Delete a site template
 */
export const useDeleteSiteTemplate = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSiteTemplate>>,
      TError,
      { templateId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSiteTemplate>>,
  TError,
  { templateId: string },
  TContext
> => {
  const mutationOptions = getDeleteSiteTemplateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Starts a background job to create a new site from a template. Supports location, label, metadata, and placeholder replacements.
 * @summary Launch site from template
 */
export const createSiteTemplateSite = (
  templateId: string,
  createSiteFromTemplateRequest: CreateSiteFromTemplateRequest,
  signal?: AbortSignal
) => {
  return customFetch<TaskIDResponse>({
    url: `/v1/sites/templates/${templateId}/sites`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSiteFromTemplateRequest,
    signal,
  });
};

export const getCreateSiteTemplateSiteMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteTemplateSite>>,
    TError,
    { templateId: string; data: CreateSiteFromTemplateRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteTemplateSite>>,
  TError,
  { templateId: string; data: CreateSiteFromTemplateRequest },
  TContext
> => {
  const mutationKey = ['createSiteTemplateSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteTemplateSite>>,
    { templateId: string; data: CreateSiteFromTemplateRequest }
  > = props => {
    const { templateId, data } = props ?? {};

    return createSiteTemplateSite(templateId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/templates/{templateId}/sites` },
    {
      operationId: 'app.controllers.site_templates_controller.create_site_template_site',
      operationName: 'createSiteTemplateSite',
    }
  );

  return customOptions;
};

export type CreateSiteTemplateSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSiteTemplateSite>>
>;
export type CreateSiteTemplateSiteMutationBody = CreateSiteFromTemplateRequest;
export type CreateSiteTemplateSiteMutationError = Error;

/**
 * @summary Launch site from template
 */
export const useCreateSiteTemplateSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteTemplateSite>>,
      TError,
      { templateId: string; data: CreateSiteFromTemplateRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteTemplateSite>>,
  TError,
  { templateId: string; data: CreateSiteFromTemplateRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteTemplateSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
